import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "../lib/format";
import {
    ShieldCheck, Building2, Users as UsersIcon, RefreshCw, Plus,
    Ban, Play, KeyRound,
} from "lucide-react";

const ROLES = ["super_admin", "admin", "employee"];

export default function SuperAdmin() {
    const { user } = useAuth();
    const [tab, setTab] = useState("branches");
    if (user?.role !== "super_admin") {
        return <div className="p-8 text-sm text-slate-500">Super Admin access required.</div>;
    }
    return (
        <div className="space-y-5" data-testid="super-admin-page">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand-soft grid place-items-center">
                    <ShieldCheck className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-royal font-semibold">
                        Super Admin
                    </div>
                    <h2 className="font-display font-bold text-slate-900 text-xl">
                        Core Controls
                    </h2>
                </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="sa-tabs">
                {[
                    { key: "branches", label: "Branches", icon: Building2 },
                    { key: "users",    label: "Users & Roles", icon: UsersIcon },
                ].map((t) => {
                    const active = tab === t.key;
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`h-9 rounded-full px-3.5 text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                                active
                                    ? "bg-brand-navy text-white border-brand-navy"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                            }`}
                            data-testid={`sa-tab-${t.key}`}
                        >
                            <Icon className="h-3.5 w-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "branches" && <BranchesPanel />}
            {tab === "users" && <UsersPanel />}
        </div>
    );
}

/* ---------- Branches ---------- */
function BranchesPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [form, setForm] = useState({ code: "", name: "", address: "" });

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/branches");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function createBranch() {
        try {
            await api.post("/branches", form);
            toast.success("Branch created");
            setShowNew(false);
            setForm({ code: "", name: "", address: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function toggleActive(b) {
        try {
            await api.patch(`/branches/${b.id}`, { is_active: !b.is_active });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <Card className="rounded-2xl border-slate-100 p-4 sm:p-5" data-testid="sa-branches">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Branches
                    </div>
                    <h3 className="mt-0.5 font-display font-bold text-slate-900 text-lg">
                        Branches Directory
                    </h3>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} className="h-9 rounded-lg">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                    </Button>
                    <Button size="sm" onClick={() => setShowNew(true)} className="h-9 rounded-lg bg-brand-navy hover:bg-brand-royal"
                        data-testid="sa-branch-new-btn">
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> New Branch
                    </Button>
                </div>
            </div>
            <div className="mt-4 overflow-x-auto">
                {loading ? (
                    <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400" data-testid="sa-branches-empty">
                        No branches yet.
                    </div>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2 font-semibold">Code</th>
                                <th className="text-left px-3 py-2 font-semibold">Name</th>
                                <th className="text-left px-3 py-2 font-semibold">Address</th>
                                <th className="text-left px-3 py-2 font-semibold">Status</th>
                                <th className="text-right px-3 py-2 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((b) => (
                                <tr key={b.id} data-testid={`sa-branch-${b.id}`}>
                                    <td className="px-3 py-2 font-mono uppercase text-slate-700">{b.code}</td>
                                    <td className="px-3 py-2 font-semibold text-slate-900">{b.name}</td>
                                    <td className="px-3 py-2 text-slate-600">{b.address || "—"}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={b.is_active
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                            : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100"}>
                                            {b.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <Button size="sm" variant="outline"
                                            className="h-8 rounded-lg text-xs"
                                            onClick={() => toggleActive(b)}
                                            data-testid={`sa-branch-toggle-${b.id}`}
                                        >
                                            {b.is_active ? "Disable" : "Enable"}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent data-testid="sa-branch-new-dialog">
                    <DialogHeader>
                        <DialogTitle>New Branch</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Code</Label>
                            <Input
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                placeholder="e.g. HQ01"
                                data-testid="sa-branch-code"
                            />
                        </div>
                        <div>
                            <Label>Name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="sa-branch-name"
                            />
                        </div>
                        <div>
                            <Label>Address (optional)</Label>
                            <Input
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                data-testid="sa-branch-address"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                        <Button onClick={createBranch}
                            className="bg-brand-navy hover:bg-brand-royal"
                            data-testid="sa-branch-save">
                            Create branch
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

/* ---------- Users & Roles ---------- */
function UsersPanel() {
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [newForm, setNewForm] = useState({ username: "", name: "", password: "", role: "employee" });
    const [action, setAction] = useState(null); // { kind: 'assign'|'disable'|'enable', user }

    async function load() {
        setLoading(true);
        try {
            const [u, b] = await Promise.all([
                api.get("/users"),
                api.get("/branches"),
            ]);
            setUsers(Array.isArray(u.data) ? u.data : []);
            setBranches(Array.isArray(b.data) ? b.data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return users;
        return users.filter((u) =>
            (u.username || "").toLowerCase().includes(s)
            || (u.name || "").toLowerCase().includes(s)
        );
    }, [users, q]);

    async function createUser() {
        try {
            await api.post("/users", newForm);
            toast.success("User created");
            setShowNew(false);
            setNewForm({ username: "", name: "", password: "", role: "employee" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const branchLabelMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.code])), [branches]);

    return (
        <Card className="rounded-2xl border-slate-100 p-4 sm:p-5" data-testid="sa-users">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Users
                    </div>
                    <h3 className="mt-0.5 font-display font-bold text-slate-900 text-lg">
                        Users, Roles & Branch Assignment
                    </h3>
                </div>
                <div className="flex gap-2">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search…"
                        className="h-9 w-56 rounded-lg"
                        data-testid="sa-users-search"
                    />
                    <Button variant="outline" size="sm" onClick={load} className="h-9 rounded-lg">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                    </Button>
                    <Button size="sm" onClick={() => setShowNew(true)}
                        className="h-9 rounded-lg bg-brand-navy hover:bg-brand-royal"
                        data-testid="sa-user-new-btn">
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> New User
                    </Button>
                </div>
            </div>
            <div className="mt-4 overflow-x-auto">
                {loading ? (
                    <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2 font-semibold">Username</th>
                                <th className="text-left px-3 py-2 font-semibold">Name</th>
                                <th className="text-left px-3 py-2 font-semibold">Role</th>
                                <th className="text-left px-3 py-2 font-semibold">Branches</th>
                                <th className="text-left px-3 py-2 font-semibold">Status</th>
                                <th className="text-right px-3 py-2 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((u) => (
                                <tr key={u.id} data-testid={`sa-user-${u.id}`}>
                                    <td className="px-3 py-2 font-mono uppercase text-slate-800">{u.username}</td>
                                    <td className="px-3 py-2 text-slate-800 font-semibold">{u.name}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={
                                            u.role === "super_admin" ? "bg-brand-soft text-brand-royal border-brand-soft hover:bg-brand-soft"
                                            : u.role === "admin" ? "bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-50"
                                            : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100"
                                        }>
                                            {u.role}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600">
                                        {(u.branch_ids || []).map((id) => branchLabelMap[id] || id.slice(0, 6)).join(", ") || "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                        {u.disabled ? (
                                            <Badge className="bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-50">Disabled</Badge>
                                        ) : (
                                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">Active</Badge>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex gap-1.5">
                                            <Button size="sm" variant="outline"
                                                className="h-8 rounded-lg text-xs"
                                                onClick={() => setAction({ kind: "assign", user: u })}
                                                data-testid={`sa-user-assign-${u.id}`}
                                            >
                                                <KeyRound className="h-3.5 w-3.5 mr-1" /> Assign
                                            </Button>
                                            {u.disabled ? (
                                                <Button size="sm" variant="outline"
                                                    className="h-8 rounded-lg text-xs text-emerald-700"
                                                    onClick={() => setAction({ kind: "enable", user: u })}
                                                    data-testid={`sa-user-enable-${u.id}`}
                                                >
                                                    <Play className="h-3.5 w-3.5 mr-1" /> Enable
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="outline"
                                                    className="h-8 rounded-lg text-xs text-rose-700"
                                                    onClick={() => setAction({ kind: "disable", user: u })}
                                                    data-testid={`sa-user-disable-${u.id}`}
                                                >
                                                    <Ban className="h-3.5 w-3.5 mr-1" /> Disable
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* New user */}
            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent data-testid="sa-user-new-dialog">
                    <DialogHeader>
                        <DialogTitle>Create User</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Username</Label>
                            <Input value={newForm.username}
                                onChange={(e) => setNewForm({ ...newForm, username: e.target.value })}
                                data-testid="sa-user-new-username" />
                        </div>
                        <div>
                            <Label>Name</Label>
                            <Input value={newForm.name}
                                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                                data-testid="sa-user-new-name" />
                        </div>
                        <div>
                            <Label>Password</Label>
                            <Input type="password" value={newForm.password}
                                onChange={(e) => setNewForm({ ...newForm, password: e.target.value })}
                                data-testid="sa-user-new-password" />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <select
                                value={newForm.role}
                                onChange={(e) => setNewForm({ ...newForm, role: e.target.value })}
                                className="mt-1.5 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                                data-testid="sa-user-new-role"
                            >
                                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                        <Button onClick={createUser} className="bg-brand-navy hover:bg-brand-royal"
                            data-testid="sa-user-new-save">
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Action dialog (reauth + reason) */}
            {action && (
                <ActionDialog
                    action={action}
                    onClose={() => setAction(null)}
                    branches={branches}
                    onDone={load}
                />
            )}
        </Card>
    );
}

function ActionDialog({ action, onClose, branches, onDone }) {
    const u = action.user;
    const [password, setPassword] = useState("");
    const [reason, setReason] = useState("");
    const [role, setRole] = useState(u.role || "employee");
    const [branchIds, setBranchIds] = useState(u.branch_ids || []);
    const [busy, setBusy] = useState(false);

    async function submit() {
        setBusy(true);
        try {
            if (action.kind === "assign") {
                if (!password) return toast.error("Enter your password to confirm");
                if (!reason.trim()) return toast.error("Provide a reason for this change");
                await api.post(`/super-admin/users/${u.id}/assign`, {
                    role,
                    branch_ids: branchIds,
                    password_confirm: password,
                    reason: reason.trim(),
                });
                toast.success("Role/branches updated");
            } else if (action.kind === "disable") {
                if (!password) return toast.error("Enter your password to confirm");
                if (!reason.trim()) return toast.error("Reason is required to disable a user");
                await api.post(`/super-admin/users/${u.id}/disable`, {
                    password_confirm: password,
                    reason: reason.trim(),
                });
                toast.success("User disabled");
            } else if (action.kind === "enable") {
                await api.post(`/super-admin/users/${u.id}/enable`);
                toast.success("User enabled");
            }
            onDone();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function toggleBranch(id) {
        setBranchIds((v) => v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);
    }

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="sa-user-action-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {action.kind === "assign" ? "Assign Role & Branches"
                         : action.kind === "disable" ? "Disable User"
                         : "Enable User"} — {u.name || u.username}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    {action.kind === "assign" && (
                        <>
                            <div>
                                <Label>Role</Label>
                                <select value={role} onChange={(e) => setRole(e.target.value)}
                                    className="mt-1.5 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                                    data-testid="sa-assign-role">
                                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Branches</Label>
                                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="sa-assign-branches">
                                    {branches.length === 0 && (
                                        <div className="text-xs text-slate-400">No branches yet. Create one first.</div>
                                    )}
                                    {branches.map((b) => {
                                        const active = branchIds.includes(b.id);
                                        return (
                                            <button
                                                key={b.id}
                                                type="button"
                                                onClick={() => toggleBranch(b.id)}
                                                className={`h-8 rounded-full px-3 text-[11px] font-semibold border ${
                                                    active
                                                        ? "bg-brand-navy text-white border-brand-navy"
                                                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                                }`}
                                                data-testid={`sa-assign-branch-${b.code}`}
                                            >
                                                {b.code}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                    {(action.kind === "assign" || action.kind === "disable") && (
                        <>
                            <div>
                                <Label>Reason</Label>
                                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                                    data-testid="sa-action-reason" />
                            </div>
                            <div>
                                <Label>Your password (to confirm)</Label>
                                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                    data-testid="sa-action-password" />
                            </div>
                        </>
                    )}
                    {action.kind === "enable" && (
                        <p className="text-sm text-slate-600">
                            This will re-enable <span className="font-semibold">{u.username}</span> and allow them to log in.
                        </p>
                    )}
                    {u.disabled_at && action.kind === "assign" && (
                        <div className="text-[11px] text-rose-600">
                            Note: this user is currently disabled since {formatDateTime(u.disabled_at)}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy}
                        className={action.kind === "disable"
                            ? "bg-rose-600 hover:bg-rose-700"
                            : "bg-brand-navy hover:bg-brand-royal"}
                        data-testid="sa-action-submit"
                    >
                        {busy ? "Saving…" : "Confirm"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
